import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

import {
  isRetiredLegacySettingsTab,
  legacySettingsTabRouteName,
  WORKSPACE_SETTINGS_ROUTE,
} from '@/features/workspace-settings'

// v3's settings URLs, kept reachable (ORP-19905 — ORP-18893's AC #1).
//
// v4 renamed the surface to `/settings` and never carried the old path, so every legacy settings
// URL a user holds — bookmark, mail, support macro — hit the catch-all (`index.ts`) and landed on
// `/summary`. Measured on v3 `origin/main`: SEVEN forms, 17 call sites, all path-based since
// `7ec0fead` moved settings off `?selected_tab` (2026-07-17):
//
//   /workspace-settings/                            (6)  → the index
//   /workspace-settings/connections                 (5)  → 12's pane (ORP-19897)
//   /workspace-settings/workspace/basics            (4)  → 22's pane
//   /workspace-settings/workspace/creative-ai       (3)  → 23's registered pane
//   /workspace-settings/credits                     (2)  → 11, unported
//   /workspace-settings/workspace/negative-keywords (1)  → 30's live pane
//
// ⚠ This is what makes ORP-19899's `?selected_tab` shim reachable at all: a legacy URL never got
// to `/settings`, so the shim could not fire on it. Hence `query` is carried through below — drop
// it and the shim dies with it.
//
// No second resolver: the legacy path's last meaningful segment is translated by the same shim
// table (`connections`, `basics`, and `workspace` → Basics, exactly as v3's parent tab resolved to
// its first child). `negative-keywords` is the one explicit slug alias because its path token is not
// the v3 display label. Unknown segments take the same §14 fallback — the user's first visible pane.

/** The last non-empty segment of the legacy sub-path — `workspace/basics` → `basics`. */
function lastSegment(params: RouteLocationNormalized['params']): string {
  const raw = params.legacyPath
  const segments = Array.isArray(raw) ? raw : [raw ?? '']
  return segments.filter(Boolean).at(-1) ?? ''
}

export const legacySettingsRoutes: RouteRecordRaw[] = [
  {
    // Registered BEFORE the catch-all, which would otherwise swallow it (same ordering rule
    // `callbacks.spec.ts` pins for `/callback`). The optional repeated param matches the bare
    // `/workspace-settings` and every depth beneath it in one record.
    path: '/workspace-settings/:legacyPath(.*)*',
    name: 'legacy-workspace-settings',
    redirect: (to) => {
      const segment = lastSegment(to.params)
      // A pane that left the app (ORP-27468's AI assistants tab) lands on home, as in v3.
      if (isRetiredLegacySettingsTab(segment)) return { path: '/' }
      const routeName = legacySettingsTabRouteName(segment)
      return { name: routeName ?? WORKSPACE_SETTINGS_ROUTE.name, query: to.query }
    },
  },
]
